import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createGlobalStyle } from 'styled-components';
import Recaptcha from 'reaptcha';
import Stats from 'frontend_stats';
import FullstoryUtil from 'fullstory_util';
import { fetchCountryCode } from 'geo_util';
import { captureMessage } from 'error_tracker';

import ResponsiveProvider from 'core/providers/ResponsiveProvider';
import ErrorBoundary from 'core/components/ErrorBoundary';
import ApiRequest from 'core/api/request';
import FB from 'core/utils/facebook';
import GUtil from 'core/utils/google';
import { saveCookie, checkCookie } from 'core/utils/loginCookies';
import { HeapUtil, promiser, gaEvent } from 'core/utils';

const HideRecaptchaBadgeStyle = createGlobalStyle`
	.grecaptcha-badge {
		display: none !important;
	}
`;


class LoginContainer extends Component {
	/**
	* There could be 1+ instances of this component on a page
	* And we only ever need 1 instance of this listener / loader
	*/
	static loginIntentListener = false;

	state = {
		outsideUsa: false,
		signInError: false,
		forgotError: false,
		forgotSuccess: false,
		loading: false,
		captchaReady: false,
		createAccountError: false,
		createAccountErrorCode: '',
		createAccountMsg: '',
		accountCookied: false,
	}

	// We will use these 3 to move data internally
	// for captcha reasons and to use the token with our backend
	captcha = null;
	data = {};
	url = '';

	// Will set true if form submission happens before script is ready
	waitingForCaptcha = false;

	forgotHandler = email => {
		this.setState({ loading: true });
		const { contextUsage } = this.props;

		const data = { email: email };
		ApiRequest.post('/customer/forgot', data)
			.then(({ email, message }) => {
				Stats.trackGeneric(contextUsage, {
					step_name: 'bubble_forgot'
				});

				Stats.trackGeneric('login-forgot', {
					step_name: contextUsage,
				});
				this.setState({
					forgotSuccess: message === 'ok',
					forgotError: message !== 'ok',
					loading: false,
				});
			})
			.catch(e => {
				const error = e["errors"][0];
				Stats.trackGeneric('mp_error', {
					step_name: 'bubble_forgot',
					error_message: error.message,
					error_code: error.error_code
				});
				Stats.trackGeneric('login-forgot-error', {
					step_name: contextUsage,
					error_message: error.message,
					error_code: error.error_code
				});
				this.setState({ forgotError: true, loading: false });
			});
	}

	verifyCallback = recaptchaToken => {
		const { data, url } = this;
		const { contextUsage } = this.props;

		const fullData = {
			...data,
			recaptchaToken,
		};

		// Prevent callbacks from other places
		if (!url) return;
		const isLogin = url === '/customer/login';
		const isCreate = url === '/customer/register';

		const mode = isCreate ? 'register' : 'login';

		ApiRequest.post(url, fullData)
			.then(res => {
				// make user data available for SPAs
				window.cj.user = res;

				saveCookie();

				Stats.trackGeneric(mode, {
					step_name: contextUsage,
				});
				
				gaEvent(mode, contextUsage);

				HeapUtil.identify();

				if(window && window.gtag) {
					// Adwords tag for login or sign-up
					window.gtag('event', 'conversion', {
						'send_to': 'AW-957505549/SFEpCO7Gi8kBEI3AycgD',
					});
				}

				// Login success, hit up that callback
				this.props.onLoginSuccess(res);
			})
			.catch(e => {
				const error = e["errors"][0];
				Stats.trackGeneric('mp_error', {
					step_name: 'bubble_' + mode,
					error_message: error.message,
					error_code: error.error_code
				});
				Stats.trackGeneric(`${mode}-error`, {
					step_name: contextUsage,
					error_message: error.message,
					error_code: error.error_code
				});
				if (isLogin) {
					this.setState({ signInError: true, loading: false });
				}

				if (isCreate) {
					this.setState({
						createAccountError: true,
						createAccountErrorCode: error.error_code,
						createAccountMsg: error.message,
						loading: false,
					});
				}

				this.url = '';
			});
	};

	completeLogin = async () => {
		if (!this.useRecaptcha()) {
			this.verifyCallback('faketoken');
			return;
		}

		const { captcha } = this;
		const [e1, r1] = await promiser(captcha.reset());
		const [e2, r2] = await promiser(captcha.execute());
		if (e1 || e2) {
			// Let recaptchaLoadCallback know to finish the job...
			this.waitingForCaptcha = true;
			// Catch the promise rejection in case recaptcha isn't loaded yet
			// And report to Statjoy + Fullstory
			let stepName = this.url.split("/").pop(); // login|register
			Stats.trackGeneric('recaptcha_unavailable', { step_name: stepName });
			FullstoryUtil.sendEvent('Waiting for Recaptcha');
		}
	}

	signInHandler = (email, signInPassword) => {
		this.setState({ loading: true });
		this.data = {
			email: email,
			password: signInPassword,
		};
		this.url = '/customer/login';

		this.completeLogin();
	}

	createAccountHandler = (email, createAccountPassword, fullName, wantsPromotions) => {
		this.setState({ loading: true });
		const { outsideUsa } = this.state;
		// Data for the verifyCallback
		this.data = {
			name: fullName,
			email: email,
			password: createAccountPassword,
			// Free choice if outsideUsa email tax if inside
			emailOptInStatus: outsideUsa
				? wantsPromotions
				: true,
		};
		this.url = '/customer/register';

		this.completeLogin();
	}

	// To be used by getLoginStatus on FacebookUtil
	fbAuthResponse = undefined;

	facebookClick = event => {
		event.preventDefault();
		const { contextUsage } = this.props;

		Stats.trackGeneric(contextUsage, {
			step_name: 'bubble_facebook-sso'
		});

		Stats.trackGeneric('login-facebook', {
			step_name: contextUsage
		});

		// Track FB lead
		if(window.fbq) {
			window.fbq('track', 'Lead');
		}

		this.props.onBeforeSocialLogin();

		if (this.fbAuthResponse) {
			FB.postConnectionLogin(this.fbAuthResponse);
		} else {
			FB.login();
		}

		return false;
	}

	googleClick = event => {
		event.preventDefault();
		const { contextUsage } = this.props;
		Stats.trackGeneric(contextUsage, {
			step_name: 'bubble_google-sso'
		});

		Stats.trackGeneric('login-google', {
			step_name: contextUsage
		});

		// Track FB lead
		if(window.fbq) {
			window.fbq('track', 'Lead');
		}

		this.props.onBeforeSocialLogin();

		GUtil.login();

		return false;
	}

	// To show or hide the newsletter checkbox via outsideUsa state prop
	corroborateFreedom = () => {
		fetchCountryCode()
			.then(country_code => {
				const outsideUsa = country_code !== 'US';
				if (outsideUsa !== this.state.outsideUsa) {
					this.setState({ outsideUsa });
				}
			})
			.catch(error => {
				captureMessage(`Error on fetchCountryCode: ${error}`);
			});
	}

	recaptchaLoadCallback = () => {
		this.setState({ captchaReady: true }, () => {
			if (this.waitingForCaptcha) {
				this.completeLogin();
			}
		});
	}

	checkCookie = () => {
		const accountCookied = checkCookie();
		if (accountCookied) this.setState({ accountCookied: true });
	}

	/**
	* Inject recaptcha script manually
	* to give us control over how/when to load
	*/
	injectScript = () => {
		const _inject = () => {
			const _id = 'cj-recaptcha-api';
			// Avoid loading script twice
			if (document.getElementById(_id)) return;
			const src = 'https://recaptcha.net/recaptcha/api.js?render=explicit';
			const script = document.createElement('script');

			script.id = _id;
			script.async = true;
			script.defer = true;
			script.src = src;

			if (document.head) {
				document.head.appendChild(script);
			}
		};
		// 1. Do setTimeout for 1s
		// 2. Wrap call in `requestIdleCallback`, if available
		// 3. Then load the script
		setTimeout(() => {
			"requestIdleCallback" in window
				? requestIdleCallback(_inject, { timeout: 1000 })
				: _inject();
		}, 0);
	}

	useRecaptcha () {
		const skipMode = (window.cj.skipMode || []).includes("recaptcha");
		if ((window.cj.fastMode || skipMode) && window.cj.useRecaptcha) {
			console.debug("Login will NOT work with useRecaptcha & fastMode/skipMode both true!");
		}
		return window.cj.useRecaptcha && !window.cj.fastMode && !skipMode;
	}

	componentDidMount () {
		if (window.FB) FB.getLoginStatus(this);
		this.checkCookie();
		this.corroborateFreedom();
		if (this.useRecaptcha()) {
			this.attachLoginEvents();
		} else {
			// this needs to fire so the form buttons are enabled
			this.recaptchaLoadCallback();
		}
	}

	attachLoginEvents = () => {
		// Check static prop so we only attach 1 instance of this listener
		if (LoginContainer.loginIntentListener) return;
		LoginContainer.loginIntentListener = true;

		// Manual script injection only when theres a login-intent
		if (window.cjLoginIntentReady) {
			this.injectScript();
		} else {
			window.addEventListener('loginIntent', (e) => {
				if (document.readyState === "complete") {
					// ready so inject now
					this.injectScript();
				} else {
					// wait until load event
					window.addEventListener('load', this.injectScript, { once: true });
				}
			}, { once: true });
		}
	}

	render () {
		const { children } = this.props;
		const sitekey = window.cj.googleRecaptchaSiteKey;
		const {
			verifyCallback,
			recaptchaLoadCallback,
		} = this;

		const childProps = {
			signInHandler: this.signInHandler,
			forgotHandler: this.forgotHandler,
			createAccountHandler: this.createAccountHandler,
			facebookClick: this.facebookClick,
			googleClick: this.googleClick,
			...this.state,
		};

		return (
			<ErrorBoundary>
				<ResponsiveProvider>
					{ children(childProps) }
					<Recaptcha
						sitekey={sitekey}
						size="invisible"
						onLoad={recaptchaLoadCallback}
						onVerify={verifyCallback}
						ref={e => (this.captcha = e)}
						inject={false/* do this manually */}
					/>
					<HideRecaptchaBadgeStyle />
				</ResponsiveProvider>
			</ErrorBoundary>
		);
	}

	static propTypes = {
		children: PropTypes.func.isRequired,
		onBeforeSocialLogin: PropTypes.func,
		onLoginSuccess: PropTypes.func.isRequired,
		contextUsage: PropTypes.string,
	}

	static defaultProps = {
		onBeforeSocialLogin: () => null,
		onLoginSuccess: () => {
			if (window.cj.back_url) {
				window.location = window.cj.back_url;
			} else {
				window.location.reload(true);
			}
		},
		contextUsage: 'standard-login-popup',
	}
}

export default LoginContainer;
